// src/pages/AttendanceOverview/index.js
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Row, Col, Tabs, Typography, Grid, Tag, Card, } from "antd";
import { useDispatch, useSelector } from "react-redux";
import AttendanceCalendar from "./components/AttendanceCalendar";
import PendingRequestsList from "./components/PendingRequestsList";
import MarkAttendanceTab from "./components/MarkAttendanceTab";
import ApproveAttendanceTab from "./components/ApproveAttendanceTab";
import HolidayListTab from "./components/HolidayListTab";
import ShiftManagementTab from "./components/ShiftManagementTab";
import AttendanceDetailsTab from "./components/AttendanceDetailsTab";
import TimeSheetManagementTab from "./components/TimeSheetManagementTab";
import {
  fetchCalendar,
  fetchTodaySummary,
  fetchPendingRequests,
  fetchEmployeeAttendanceSummary,
} from "./attendanceOverviewThunk";
import {
  selectAttendanceLoading,
  selectAttendanceCalendar,
  selectSelectedDate,
  selectAttendanceSummary,
  selectEmployeeAttendanceSummary,
} from "./attendanceOverviewSelectors";
import { accessChecker } from "../../lib/functions";
import { useSearchParams } from "react-router-dom";
import { setSelectedDate } from "./attendanceOverviewSlice";

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const AttendanceOverview = () => {
  const dispatch = useDispatch();
  const loading = useSelector(selectAttendanceLoading);
  const calendar = useSelector(selectAttendanceCalendar);
  const selectedDate = useSelector(selectSelectedDate);
  const attendanceSummary = useSelector(selectAttendanceSummary);
  const employeeAttendanceSummary = useSelector(selectEmployeeAttendanceSummary);
  const authInfo = useSelector((state) => state.auth.auth_info);

  const [activeTab, setActiveTab] = useState("mark");
  const [isAdminUser, isAdminUserSet] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [tabInitialized, setTabInitialized] = useState(false);

  const {useBreakpoint} = Grid;
  const screens = useBreakpoint();

  const approveParam = searchParams.get("approve");

  const isEmployee = authInfo?.employee_id && authInfo.employee_id !== 0;

  useEffect(() => {
    const hrRoles = [
      // HR role
      "ForthERP_Basic_hr",
      "ForthERP_Standard_hr",
      "ForthERP_Pro_hr",
      "PharmaERP_Basic_hr",
      "PharmaERP_Standard_hr",
      "PharmaERP_Pro_hr",
      "PharmaBooks_Basic_hr",
      "PharmaBooks_Standard_hr",
      "PharmaBooks_Pro_hr",
      "ForthBooks_Basic_hr",
      "ForthBooks_Standard_hr",
      "ForthBooks_Pro_hr",
      "HRPayroll_Standard_hr",
      "HRPayroll_Pro_hr",
      "SuperERP_hr",

      // Admin role
      "ForthERP_Basic_admin",
      "ForthERP_Standard_admin",
      "ForthERP_Pro_admin",
      "PharmaERP_Basic_admin",
      "PharmaERP_Standard_admin",
      "PharmaERP_Pro_admin",
      "PharmaBooks_Basic_admin",
      "PharmaBooks_Standard_admin",
      "PharmaBooks_Pro_admin",
      "ForthBooks_Basic_admin",
      "ForthBooks_Standard_admin",
      "ForthBooks_Pro_admin",
      "HRPayroll_Standard_admin",
      "HRPayroll_Pro_admin",
      "SuperERP_admin",
      "SuperERP_super_admin",
    ];

    const userRoles = authInfo.userInfo.user_role_names
      ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
      : [];

    const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

    isAdminUserSet(isAdminHR);
  }, []);

  useEffect(() => {
    switch (activeTab) {
      case "mark":
        dispatch(fetchCalendar());
        if (isAdminUser) {
          dispatch(fetchTodaySummary());
        }

        if (isEmployee) {
          dispatch(setSelectedDate(dayjs().format("YYYY-MM-DD")));
        }
        // isEmployee && dispatch(fetchEmployeeAttendanceSummary({
        // date: dayjs().format("YYYY-MM-DD")}));
        dispatch(fetchPendingRequests());
        break;
      case "approve":
        // handled inside component
        break;
      case "holiday":
        dispatch(fetchCalendar());
        break;
      case "shift":
        break;
      case "details":
        break;
      case "timesheet":
        break;
      default:
        break;
    }
  }, [activeTab, dispatch, isEmployee, isAdminUser]);
  

  useEffect(() => {
    if (selectedDate && isAdminUser) {
      dispatch(
        fetchTodaySummary({
          date: selectedDate,
        })
      );
    }
    if (selectedDate && isEmployee) {
      dispatch(
        fetchEmployeeAttendanceSummary({
          date: selectedDate,
        })
      );
    }


  }, [dispatch, selectedDate, isEmployee, isAdminUser]);

  const handleTabChange = (key) => setActiveTab(key);
  

  useEffect(() => {
    if (tabInitialized) return;

    if (approveParam !== null) {
      setActiveTab("approve");
      setTabInitialized(true);
      return;
    }
    
    if (isEmployee) {
      setActiveTab("mark");
    } else if (isAdminUser) {
      if (accessChecker("approve_attendance")) {
        setActiveTab("approve");
      } else {
        setActiveTab("holiday");
      }
    } else {
      setActiveTab("overview");
    }

    setTabInitialized(true);
  }, [isEmployee, isAdminUser, approveParam, setSearchParams, tabInitialized]);


  const styles = {
  summaryCard: {
    borderRadius: 14,
    boxShadow: "0 4px 16px rgba(0,0,0,.08)",
    transition: "all .25s ease",
    height: "100%",
    border: "1px solid #f0f0f0",
  },

  detailCard: {
    borderRadius: 12,
    height: "100%",
    border: "1px solid #f0f0f0",
  },

  title: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },

  tag: {
    marginTop: 10,
    borderRadius: 20,
    padding: "4px 12px",
  },

  notes: {
    fontSize: 14,
    color: "#595959",
    lineHeight: 1.8,
  },

  cardBody: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
};

  return (
    <div style={{ padding: screens.xs ? 10 : 24, maxWidth: "100%", overflowX: "hidden" }}>
      {/* <Title level={2}>Attendance Overview</Title> */}

      <Card bordered style={{ minHeight: 520 }}>
        {/* <Title level={2}>Attendance Overview</Title> */}
        <Tabs
          defaultActiveKey="mark"
          type="line"
          activeKey={activeTab}
          onChange={handleTabChange}
          tabBarGutter={screens.md ? undefined : 12}
          size={screens.md ? "middle" : "small"}
        >
          {isEmployee && (
            <>
              {/* <TabPane tab="Mark Attendance" key="mark">
                <Row gutter={24}>

                  
                  <Col span={24}>
                    <MarkAttendanceTab />
                  </Col>

                  
                  <Col xs={24} lg={16}>
                    <Card
                      title="Attendance Calendar"
                      className="attendance-overview-card"
                    >
                      <AttendanceCalendar
                        calendarData={calendar}
                        loading={loading}
                      />
                    </Card>
                  </Col>

                  <Col xs={24} lg={8}>
                    <Card
                      title="Pending Requests"
                      className="attendance-overview-card"
                      style={{ height: "100%" }}
                    >
                      <PendingRequestsList />
                    </Card>
                  </Col>                
                </Row>
              </TabPane> */}
              <TabPane tab="Mark Attendance" key="mark">
                  <Row gutter={24}>
                      <Col xs={24} md={10} lg={8}>
                          <AttendanceCalendar
                              calendarData={calendar}
                              loading={loading}
                          />
                      </Col>

                      <Col xs={24} md={14} lg={16}>
                          <MarkAttendanceTab />
                          <PendingRequestsList />
                      </Col>
                  </Row>
              </TabPane>
              {accessChecker("approve_attendance") && (
                <TabPane tab="Approve Attendance" key="approve">
                  <ApproveAttendanceTab />
                </TabPane>
              )}

              <TabPane tab="Holiday List" key="holiday">
                <HolidayListTab calendarData={calendar} loading={loading} />
              </TabPane>

              <TabPane tab="Shift Management" key="shift">
                <ShiftManagementTab />
              </TabPane>

              <TabPane tab="Attendance Details" key="details">
                <AttendanceDetailsTab />
              </TabPane>

              <TabPane tab="Time Sheet Management" key="timesheet">
                <TimeSheetManagementTab />
              </TabPane>
            </>
          )}
          {!isEmployee && isAdminUser && (
            <>
              {accessChecker("approve_attendance") && (
                <TabPane tab="Approve Attendance" key="approve">
                  <ApproveAttendanceTab />
                </TabPane>
              )}
              <TabPane tab="Holiday List" key="holiday">
                <HolidayListTab calendarData={calendar} loading={loading} />
              </TabPane>
              <TabPane tab="Shift Management" key="shift">
                <ShiftManagementTab />
              </TabPane>
              <TabPane tab="Attendance Details" key="details">
                <AttendanceDetailsTab />
              </TabPane>

              <TabPane tab="Time Sheet Management" key="timesheet">
                <TimeSheetManagementTab />
              </TabPane>
            </>
          )}
          {!isEmployee && !isAdminUser && (
            <TabPane tab="Overview" key="overview">
              <div>No access to attendance features.</div>
            </TabPane>
          )}
        </Tabs>
      </Card>
    </div>
  );
};

export default AttendanceOverview;
